'use client';

import { c as _c } from "react/compiler-runtime";
import type { MergeabilityStatus } from '@/lib/hooks/mergeability-status/get-mergeability-status.server';
import type { PrV2SummaryResponse } from '@/lib/pr-v2/types';
import type { ReactNode } from 'react';
import type { PrV2MetadataSummary } from '../shared/pr-v2-metadata';
import { PrV2UserNameLabel } from '../shared/pr-v2-user-name-label';
import { PrV2BranchRefs } from './pr-v2-branch-refs';
import { PrV2HeaderDiffStats } from './pr-v2-header-diff-stats';
import { PrV2MergeabilityBadge, PrV2MergeabilityBadgeSkeleton } from './pr-v2-mergeability-badge';
import { PrV2Title } from './pr-v2-title';
import { PrUpdatedAt } from '@/app/(home)/inbox/components/pr-updated-at';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { getMergeabilityStatusBadgeClasses, getMergeabilityStatusBadgeText } from '@/lib/hooks/mergeability-status/presentation';
import { useMergeabilityStatus } from '@/lib/hooks/mergeability-status/use-mergeability-status';
import { cn } from '@/lib/utils';
type PrV2HeaderProps = {
  owner: string;
  repo: string;
  number: string;
  summary: PrV2SummaryResponse['pullRequest'];
  metadata: PrV2MetadataSummary;
  readOnly?: boolean;
  /** Smaller type and spacing for the resizable /code PR pane. */
  compact?: boolean;
  initialMergeabilityStatus?: MergeabilityStatus | null;
  /**
   * The line under the title. Defaults to the head → base branch refs; pass `null` when the
   * branches are shown elsewhere (the /code PR tab lists them as a property row).
   */
  branchRefs?: ReactNode;
};
function getFallbackMergeabilityBadge(summary: PrV2SummaryResponse['pullRequest']) {
  if (summary.merged) {
    return {
      text: 'Merged',
      className: 'bg-purple-900/50 text-purple-300'
    };
  }
  if (summary.draft) {
    return {
      text: 'Draft',
      className: 'bg-zinc-800 text-zinc-300'
    };
  }
  if (summary.state === 'closed') {
    return {
      text: 'Closed',
      className: 'bg-red-900/50 text-red-300'
    };
  }
  return null;
}
export function PrV2Header(t0) {
  const $ = _c(64);
  const {
    owner,
    repo,
    number,
    summary,
    metadata,
    readOnly: t1,
    compact: t2,
    initialMergeabilityStatus: t3,
    branchRefs
  } = t0;
  const readOnly = t1 === undefined ? false : t1;
  const compact = t2 === undefined ? false : t2;
  const initialMergeabilityStatus = t3 === undefined ? null : t3;
  const createdAt = metadata.createdAt ?? summary.githubUpdatedAt ?? summary.cachedAt;
  const updatedAt = metadata.updatedAt ?? summary.githubUpdatedAt ?? createdAt;
  let t4;
  if ($[0] !== summary.author.avatarUrl) {
    t4 = summary.author.avatarUrl?.trim() || undefined;
    $[0] = summary.author.avatarUrl;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const authorAvatarUrl = t4;
  let t5;
  if ($[2] !== initialMergeabilityStatus) {
    t5 = {
      initialData: initialMergeabilityStatus
    };
    $[2] = initialMergeabilityStatus;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  const {
    status
  } = useMergeabilityStatus(owner, repo, number, t5);
  let t6;
  if ($[4] !== summary) {
    t6 = getFallbackMergeabilityBadge(summary);
    $[4] = summary;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  const fallbackMergeabilityBadge = t6;
  const t7 = compact ? "space-y-2" : "space-y-4";
  let t8;
  if ($[6] !== compact || $[7] !== number || $[8] !== owner || $[9] !== readOnly || $[10] !== repo || $[11] !== summary.number || $[12] !== summary.title) {
    t8 = <PrV2Title owner={owner} repo={repo} number={number} prNumber={summary.number} title={summary.title} readOnly={readOnly} compact={compact} />;
    $[6] = compact;
    $[7] = number;
    $[8] = owner;
    $[9] = readOnly;
    $[10] = repo;
    $[11] = summary.number;
    $[12] = summary.title;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== branchRefs || $[15] !== metadata || $[16] !== summary) {
    t9 = branchRefs === undefined ? <PrV2BranchRefs summary={summary} metadata={metadata} className="mt-1" /> : branchRefs;
    $[14] = branchRefs;
    $[15] = metadata;
    $[16] = summary;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== t8 || $[19] !== t9) {
    t10 = <div className="flex flex-col">{t8}{t9}</div>;
    $[18] = t8;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  const t11 = compact ? "gap-x-3 gap-y-1.5" : "gap-4";
  let t12;
  if ($[21] !== t11) {
    t12 = cn("flex min-w-0 flex-wrap items-center", t11);
    $[21] = t11;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== fallbackMergeabilityBadge || $[24] !== status) {
    t13 = status ? <PrV2MergeabilityBadge className={getMergeabilityStatusBadgeClasses(status)}>{getMergeabilityStatusBadgeText(status)}</PrV2MergeabilityBadge> : fallbackMergeabilityBadge ? <PrV2MergeabilityBadge className={fallbackMergeabilityBadge.className}>{fallbackMergeabilityBadge.text}</PrV2MergeabilityBadge> : <PrV2MergeabilityBadgeSkeleton />;
    $[23] = fallbackMergeabilityBadge;
    $[24] = status;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  const t14 = `${summary.author.login ?? "user"} avatar`;
  let t15;
  if ($[26] !== authorAvatarUrl || $[27] !== t14) {
    t15 = <AvatarImage src={authorAvatarUrl} alt={t14} />;
    $[26] = authorAvatarUrl;
    $[27] = t14;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  const t16 = summary.author.login ?? "??";
  let t17;
  if ($[29] !== t16) {
    t17 = t16.slice(0, 2).toUpperCase();
    $[29] = t16;
    $[30] = t17;
  } else {
    t17 = $[30];
  }
  let t18;
  if ($[31] !== t17) {
    t18 = <AvatarFallback>{t17}</AvatarFallback>;
    $[31] = t17;
    $[32] = t18;
  } else {
    t18 = $[32];
  }
  let t19;
  if ($[33] !== t15 || $[34] !== t18) {
    t19 = <Avatar className="size-4 shrink-0">{t15}{t18}</Avatar>;
    $[33] = t15;
    $[34] = t18;
    $[35] = t19;
  } else {
    t19 = $[35];
  }
  const t20 = summary.author.login;
  const t21 = compact ? "text-xs" : "text-sm";
  let t22;
  if ($[36] !== t21) {
    t22 = cn("min-w-0 truncate text-muted-foreground", t21);
    $[36] = t21;
    $[37] = t22;
  } else {
    t22 = $[37];
  }
  let t23;
  if ($[38] !== summary.author.login || $[39] !== t22) {
    t23 = <PrV2UserNameLabel login={t20} className={t22} />;
    $[38] = summary.author.login;
    $[39] = t22;
    $[40] = t23;
  } else {
    t23 = $[40];
  }
  let t24;
  if ($[41] !== t19 || $[42] !== t23) {
    t24 = <div className="flex min-w-0 items-center gap-1.5">{t19}{t23}</div>;
    $[41] = t19;
    $[42] = t23;
    $[43] = t24;
  } else {
    t24 = $[43];
  }
  const t25 = compact ? "text-xs" : "text-sm";
  let t26;
  if ($[44] !== createdAt || $[45] !== t25 || $[46] !== updatedAt) {
    t26 = <PrUpdatedAt createdAt={createdAt} updatedAt={updatedAt} className={t25} side="right" />;
    $[44] = createdAt;
    $[45] = t25;
    $[46] = updatedAt;
    $[47] = t26;
  } else {
    t26 = $[47];
  }
  let t27;
  if ($[48] !== metadata || $[49] !== number || $[50] !== owner || $[51] !== repo || $[52] !== summary) {
    t27 = <PrV2HeaderDiffStats owner={owner} repo={repo} number={number} summary={summary} metadata={metadata} />;
    $[48] = metadata;
    $[49] = number;
    $[50] = owner;
    $[51] = repo;
    $[52] = summary;
    $[53] = t27;
  } else {
    t27 = $[53];
  }
  let t28;
  if ($[54] !== t12 || $[55] !== t13 || $[56] !== t24 || $[57] !== t26 || $[58] !== t27) {
    t28 = <div className={t12}>{t13}{t24}{t26}{t27}</div>;
    $[54] = t12;
    $[55] = t13;
    $[56] = t24;
    $[57] = t26;
    $[58] = t27;
    $[59] = t28;
  } else {
    t28 = $[59];
  }
  let t29;
  if ($[60] !== t10 || $[61] !== t28 || $[62] !== t7) {
    t29 = <section className={t7}>{t10}{t28}</section>;
    $[60] = t10;
    $[61] = t28;
    $[62] = t7;
    $[63] = t29;
  } else {
    t29 = $[63];
  }
  return t29;
}